import { type FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AttachmentList } from "../../../components/AttachmentList";
import { type Breadcrumb, useBreadcrumb } from "../../../components/Breadcrumb";
import { ReactComponent as AiSparkleIcon } from "../../../components/ChatBox/ai-sparkle.svg";
import ChatBox from "../../../components/ChatBox/ChatBox";
import { ReactComponent as GmailIcon } from "../../../components/EmailCard/gmail.svg";
import { EmailContentView } from "../../../components/EmailContentView";
import { LoadingUI } from "../../../components/Loading";
import { MatchingConditionAlert } from "../../../components/matching/MatchingConditionAlert";
import { PageTitle } from "../../../components/PageTitle";
import { Button } from "../../../components/ui/button";
import { DismissibleAlert } from "../../../components/ui/dismissible-alert";
import { hasLicense, LICENSE_KEY } from "../../../constants/licenses";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import type {
  PromptType,
  Recipient,
  RecipientType,
  ReplyDraft,
} from "../../../hooks/use-email-compose";
import type {
  MatchingJobResult,
  MatchingResultItem,
} from "../../../hooks/use-free-matching-api";
import type { ProposalDraft } from "../../../hooks/use-proposal-composer";
import {
  type EmailDocument,
  RecruitmentType,
} from "../../../hooks/useEmailsRestApi";
import { getCurrentTenant } from "../../../utils/authority-utils";
import { searchGmail } from "../../../utils/gmail-utils";
import { toastError, toastPromise } from "../../../utils/toast-utils";
import { ReactComponent as CheckIcon } from "../SearchEmailsPage/components/EmailDetailModal/check.svg";
import { ReactComponent as CopyIcon } from "../SearchEmailsPage/components/EmailDetailModal/copy.svg";
import { DetailedComparisonModal } from "./components/DetailedComparisonModal";
import { MatchingResultsSection } from "./components/MatchingResultsSection";
import { ParsedHRView } from "./components/ParsedHRView";
import {
  generateMatterSummaryText,
  ParsedMatterSummaryView,
} from "./components/ParsedMatterSummaryView";
import { ParsedMatterView } from "./components/ParsedMatterView";
import { ProposalComposePane } from "./components/ProposalComposePane";
import { ReplyComposePane } from "./components/ReplyComposePane";

export type RightPanelMode = "results" | "compose" | "reply";

const BREAD_CRUMBS: Breadcrumb[] = [
  {
    label: "ホーム",
    to: "/",
  },
  {
    label: "メール",
  },
  {
    label: "検索",
    to: "/emails/search",
  },
  {
    label: "詳細",
  },
];

type EmailDetailPageProps = {
  isLoading: boolean;
  email: EmailDocument | null;
  freeWords: string[];
  isMatchingEnabled: boolean;
  hasActiveMatchingCondition: boolean;
  isMatchingLoading: boolean;
  matchingError: Error | undefined;
  matchingJob: MatchingJobResult | undefined;
  onExecuteMatching: () => void;
  onDismissMatchingError: () => void;
  // Proposal
  rightPanelMode: RightPanelMode;
  proposalDraft: ProposalDraft | null;
  isProposalGenerating: boolean;
  isProposalSending: boolean;
  proposalError: string | null;
  onPropose: (result: MatchingResultItem) => void;
  onProposalFieldChange: (field: keyof ProposalDraft, value: string) => void;
  onSendProposal: () => Promise<boolean>;
  onBackToResults: () => void;
  onCancelGeneration: () => void;
  // Reply
  replyDraft: ReplyDraft | null;
  isReplyGenerating: boolean;
  isReplySending: boolean;
  replyError: string | null;
  onReply: () => void;
  onReplyFieldChange: <K extends keyof ReplyDraft>(
    field: K,
    value: ReplyDraft[K],
  ) => void;
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (email: string, type: RecipientType) => void;
  onGenerateReplyBody: (promptType: PromptType, freeText?: string) => void;
  onSendReply: () => Promise<boolean>;
  onCancelReplyGeneration: () => void;
  // Comparison
  comparisonTarget: MatchingResultItem | null;
  onCompare: (result: MatchingResultItem) => void;
  onCloseComparison: () => void;
  // Feedback
  onFeedback?: (
    resultId: string,
    rating: number,
    comment?: string | null,
  ) => Promise<void>;
};

type TabType = "parsed" | "summary" | "raw";

export const EmailDetailPage: FC<EmailDetailPageProps> = ({
  isLoading,
  email,
  freeWords,
  isMatchingEnabled,
  hasActiveMatchingCondition,
  isMatchingLoading,
  matchingError,
  matchingJob,
  onExecuteMatching,
  onDismissMatchingError,
  rightPanelMode,
  proposalDraft,
  isProposalGenerating,
  isProposalSending,
  proposalError,
  onPropose,
  onProposalFieldChange,
  onSendProposal,
  onBackToResults,
  onCancelGeneration,
  replyDraft,
  isReplyGenerating,
  isReplySending,
  replyError,
  onReply,
  onReplyFieldChange,
  onAddRecipient,
  onRemoveRecipient,
  onGenerateReplyBody,
  onSendReply,
  onCancelReplyGeneration,
  comparisonTarget,
  onCompare,
  onCloseComparison,
  onFeedback,
}) => {
  const { setBreadcrumbItems } = useBreadcrumb();
  const [isCopied, setIsCopied] = useState(false);
  const [isChatBoxOpen, setIsChatBoxOpen] = useState(false);
  const [chatLimitError, setChatLimitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // プレミアム機能（整理された情報タブ）が利用可能かどうかをチェック
  const isPremiumFeatureEnabled = hasLicense(
    getCurrentTenant(currentUser)?.licenses,
    LICENSE_KEY.EMAIL_SEARCH,
  );

  // AI機能が利用可能かどうか（AI_HELPERライセンスまたはシステム管理者）
  const isAiHelperEnabled =
    currentUser?.isSystemAdmin === true ||
    hasLicense(getCurrentTenant(currentUser)?.licenses, LICENSE_KEY.AI_HELPER);

  // パース済みデータが存在するかどうかをチェック
  const hasParsedData =
    (email?.recruitmentType === RecruitmentType.Matter &&
      email?.matterColumn) ||
    (email?.recruitmentType === RecruitmentType.HumanResource &&
      email?.hrColumn);

  // 整理された情報タブが利用可能かどうか（データがあり、かつプレミアム機能が有効）
  const isParsedTabEnabled = hasParsedData && isPremiumFeatureEnabled;

  // デフォルトタブ：プレミアム機能が有効なら "parsed"、そうでなければ "raw"
  const [activeTab, setActiveTab] = useState<TabType>(
    isPremiumFeatureEnabled ? "parsed" : "raw",
  );

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const copyEmail = useCallback(async () => {
    if (isCopied || !email) {
      // コピー済みの場合は何もしない
      return;
    }

    let textToCopy: string;

    if (activeTab === "summary" && email.matterColumn) {
      // メール用サマリタブの場合：サマリテキストをコピー
      textToCopy = generateMatterSummaryText(email.matterColumn);
    } else {
      // メール本文タブの場合：タイトルと本文をコピー
      const title: string = email.subject || "タイトルなし";
      const mainText: string = email.content || "本文なし";
      textToCopy = [title, mainText].join("\n");
    }

    // クリップボードにテキストをコピー
    if (textToCopy) {
      window.focus();
      try {
        const successMessage =
          activeTab === "summary"
            ? "サマリをコピーしました！"
            : "メール内容をコピーしました！";
        await toastPromise(navigator.clipboard.writeText(textToCopy), {
          pending: "コピー中...",
          success: successMessage,
          error: "コピーに失敗しました。",
        });
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      } catch (_err) {
        toastError("コピーに失敗しました。");
        return;
      }
    }
  }, [isCopied, email, activeTab]);

  const handleGoBack = useCallback(() => {
    if (email?.id) {
      navigate(`/emails/search?emailId=${email.id}`);
    } else {
      navigate("/emails/search");
    }
  }, [navigate, email]);

  if (isLoading) {
    return (
      <div className="w-full flex justify-center pt-20">
        <LoadingUI type="Grid" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex justify-center mt-4">
        <div className="text-left text-gray-500 max-w-xl">
          <p>メールが見つかりません。</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <PageTitle>メール詳細</PageTitle>
        <div className="flex items-center gap-2">
          {email && (
            <Button
              size="xs"
              onClick={onReply}
              disabled={rightPanelMode === "reply"}
            >
              返信する
            </Button>
          )}
          {isMatchingEnabled && email && (
            <Button
              size="xs"
              className="btn-accent"
              onClick={onExecuteMatching}
              disabled={isMatchingLoading}
            >
              {isMatchingLoading
                ? "マッチング中..."
                : matchingJob
                  ? "再マッチング"
                  : email.recruitmentType === RecruitmentType.HumanResource
                    ? "案件とマッチング"
                    : "人材とマッチング"}
            </Button>
          )}
          <Button size="xs" onClick={handleGoBack} isOutline>
            ← 検索結果に戻る
          </Button>
        </div>
      </div>

      {/* マッチング設定未登録の案内 */}
      <MatchingConditionAlert
        show={isMatchingEnabled && !hasActiveMatchingCondition}
      />

      {/* エラーアラート（マッチング上限 / AIヘルパー上限） */}
      {matchingError && !isMatchingLoading && (
        <DismissibleAlert
          variant="error"
          className="mb-4"
          onDismiss={onDismissMatchingError}
        >
          {matchingError.message}
        </DismissibleAlert>
      )}
      {chatLimitError && (
        <DismissibleAlert
          variant="error"
          className="mb-4"
          onDismiss={() => setChatLimitError(null)}
        >
          {chatLimitError}
        </DismissibleAlert>
      )}

      {/* 横並びレイアウト: ChatBox | メール詳細 | マッチング結果 */}
      <div className="flex gap-4">
        {/* ChatBox（トグルで表示/非表示） */}
        {isChatBoxOpen && isAiHelperEnabled && (
          <div className="w-1/4 shrink-0">
            <ChatBox
              email={email}
              disabled={!isPremiumFeatureEnabled}
              onLimitError={setChatLimitError}
            />
          </div>
        )}

        {/* メール詳細（マッチング結果がある場合は 6:4 比率、ない場合は中央寄せ） */}
        <div
          className={`bg-white rounded-lg shadow-xl p-6 overflow-auto ${
            matchingJob ? "w-[60%] shrink-0" : "max-w-4xl mx-auto w-full"
          }`}
        >
          {/* ヘッダー: タイトルと差出人情報 */}
          <div className="pb-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {email.subject}
            </h2>
            <div className="text-sm text-gray-600 whitespace-pre-line">
              {`差出人: ${email.from.name} (${email.from.address})\n日付: ${email.date}`}
            </div>
          </div>

          {/* ツールバー + タブ */}
          <div className="flex items-center justify-between border-b border-gray-200 mb-4">
            {/* タブ切り替え */}
            <div className="flex">
              {hasParsedData && (
                <>
                  <button
                    type="button"
                    onClick={() => isParsedTabEnabled && setActiveTab("parsed")}
                    disabled={!isParsedTabEnabled}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "parsed" && isParsedTabEnabled
                        ? "border-blue-500 text-blue-600"
                        : !isParsedTabEnabled
                          ? "border-transparent text-gray-300 cursor-not-allowed"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    title={
                      !isParsedTabEnabled
                        ? "この機能はご利用いただけません"
                        : undefined
                    }
                  >
                    整理された情報
                  </button>
                  {/* 案件メールの場合のみメール用サマリタブを表示 */}
                  {email?.recruitmentType === RecruitmentType.Matter && (
                    <button
                      type="button"
                      onClick={() =>
                        isParsedTabEnabled && setActiveTab("summary")
                      }
                      disabled={!isParsedTabEnabled}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === "summary" && isParsedTabEnabled
                          ? "border-blue-500 text-blue-600"
                          : !isParsedTabEnabled
                            ? "border-transparent text-gray-300 cursor-not-allowed"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      title={
                        !isParsedTabEnabled
                          ? "この機能はご利用いただけません"
                          : undefined
                      }
                    >
                      メール用サマリ
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveTab("raw")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "raw"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    メール本文
                  </button>
                </>
              )}
            </div>

            {/* ツールアイコン群 */}
            <div className="flex items-center gap-1 pb-1">
              <button
                type="button"
                onClick={() =>
                  isAiHelperEnabled && setIsChatBoxOpen(prev => !prev)
                }
                disabled={!isAiHelperEnabled}
                className={`p-2 rounded-lg transition-colors ${
                  !isAiHelperEnabled
                    ? "text-gray-300 cursor-not-allowed"
                    : isChatBoxOpen
                      ? "bg-blue-100 text-blue-600"
                      : "hover:bg-gray-100 text-gray-600"
                }`}
                title={
                  isAiHelperEnabled
                    ? "AIチャット"
                    : "この機能はご利用いただけません"
                }
              >
                <AiSparkleIcon className="w-5 h-5" />
              </button>
              {email?.subject && (
                <button
                  type="button"
                  onClick={() =>
                    searchGmail(email.subject, email.originalMessageId)
                  }
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                  title="Gmailで検索"
                >
                  <GmailIcon className="w-5 h-5" />
                </button>
              )}
              {/* コピーアイコン: メール用サマリまたはメール本文タブ選択時のみ表示 */}
              {(activeTab === "summary" || activeTab === "raw") && (
                <button
                  type="button"
                  onClick={copyEmail}
                  disabled={isCopied}
                  className={`p-2 rounded-lg transition-colors ${
                    isCopied
                      ? "bg-green-100 text-green-600"
                      : "hover:bg-gray-100 text-gray-600"
                  }`}
                  title={
                    activeTab === "summary"
                      ? "サマリをコピー"
                      : "メール内容をコピー"
                  }
                >
                  {isCopied ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    <CopyIcon className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* コンテンツ表示 */}
          <div className="prose max-w-none">
            {/* パース済み情報タブ（プレミアム機能が有効な場合のみ表示） */}
            {activeTab === "parsed" && isParsedTabEnabled && (
              <div>
                {email.recruitmentType === RecruitmentType.Matter &&
                  email.matterColumn && (
                    <ParsedMatterView matterColumn={email.matterColumn} />
                  )}
                {email.recruitmentType === RecruitmentType.HumanResource &&
                  email.hrColumn && <ParsedHRView hrColumn={email.hrColumn} />}
              </div>
            )}

            {/* メール用サマリタブ（案件メール + プレミアム機能が有効な場合のみ表示） */}
            {activeTab === "summary" &&
              isParsedTabEnabled &&
              email.recruitmentType === RecruitmentType.Matter &&
              email.matterColumn && (
                <ParsedMatterSummaryView matterColumn={email.matterColumn} />
              )}

            {/* メール本文タブ（またはパースタブが無効の場合のデフォルト表示） */}
            {(activeTab === "raw" || !isParsedTabEnabled) && (
              <EmailContentView
                content={email.content}
                freeWords={freeWords}
                highlighterType="Modal"
              />
            )}
          </div>

          {/* 添付ファイル一覧 */}
          {email.hasAttachments && email.attachments && (
            <AttachmentList
              attachments={email.attachments}
              emailId={email.id}
            />
          )}
        </div>

        {/* 右パネル: マッチング結果 or 提案メール作成 or 返信メール作成 */}
        {(matchingJob ||
          (rightPanelMode === "compose" && proposalDraft) ||
          (rightPanelMode === "reply" && replyDraft)) && (
          <div className="w-[40%] shrink-0 overflow-auto">
            {rightPanelMode === "reply" && replyDraft ? (
              <ReplyComposePane
                draft={replyDraft}
                isGenerating={isReplyGenerating}
                isSending={isReplySending}
                error={replyError}
                onFieldChange={onReplyFieldChange}
                onAddRecipient={onAddRecipient}
                onRemoveRecipient={onRemoveRecipient}
                onGenerateBody={onGenerateReplyBody}
                onSend={onSendReply}
                onClose={onBackToResults}
                onCancelGeneration={onCancelReplyGeneration}
              />
            ) : rightPanelMode === "compose" && proposalDraft ? (
              <ProposalComposePane
                draft={proposalDraft}
                isGenerating={isProposalGenerating}
                isSending={isProposalSending}
                error={proposalError}
                onFieldChange={onProposalFieldChange}
                onSend={onSendProposal}
                onBackToResults={onBackToResults}
                onCancelGeneration={onCancelGeneration}
              />
            ) : matchingJob ? (
              <MatchingResultsSection
                job={matchingJob}
                onPropose={onPropose}
                onCompare={onCompare}
                onFeedback={onFeedback}
              />
            ) : null}
          </div>
        )}
      </div>

      {/* 詳細比較モーダル */}
      {comparisonTarget && email && (
        <DetailedComparisonModal
          isOpen={!!comparisonTarget}
          onClose={onCloseComparison}
          sourceEmail={email}
          matchingResult={comparisonTarget}
        />
      )}
    </div>
  );
};
